/**
 * WeeklyCalendar Component
 *
 * Displays a 7×3 grid layout for weekly meal planning (7 days × 3 meals per day).
 * Shows day headers (Monday-Sunday) and meal labels (Breakfast, Lunch, Dinner).
 *
 * On mobile and portrait tablet (< 1024px or landscape width < 1024px in portrait):
 * the table is transposed — rows = days, columns = meal types.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMealPlanStore } from '../../stores/mealPlanStore';
import type { MealType, DayOfWeek, MealSlot as MealSlotType } from '../../types/mealPlan';
import { MealSlot } from './MealSlot';
import { RecipePicker } from './RecipePicker';
import './mealPlan.css';

/** Returns true when we should use the transposed (days-as-rows) layout. */
function useTransposedLayout(): boolean {
  const query = '(max-width: 1023px), (max-height: 1023px) and (orientation: portrait)';
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return matches;
}

export function WeeklyCalendar() {
  const { t, i18n } = useTranslation('mealPlan');
  const { currentMealPlan, isLoading, error, navigateWeek, getCurrentWeekPlan } = useMealPlanStore();
  const isTransposed = useTransposedLayout();

  // Fetch current week's meal plan on mount
  useEffect(() => {
    getCurrentWeekPlan().catch(() => {});
  }, [getCurrentWeekPlan]);
  const [selectedSlot, setSelectedSlot] = useState<{
    dayIndex: DayOfWeek;
    mealType: MealType;
    slot: MealSlotType | null;
  } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const DAYS_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
  const MEALS: MealType[] = ['breakfast', 'lunch', 'dinner'];

  // Helper function to get meal slot for a specific day and meal type
  const getMealSlot = (dayIndex: DayOfWeek, mealType: MealType) => {
    if (!currentMealPlan?.meal_slots) return null;

    return currentMealPlan.meal_slots.find(
      (slot) => slot.day === dayIndex && slot.meal_type === mealType
    ) || null;
  };

  // Helper function to format date range display
  const formatDateRange = () => {
    if (!currentMealPlan) return '';

    const startDate = new Date(currentMealPlan.start_date);
    const endDate = new Date(currentMealPlan.end_date);

    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
    const startStr = startDate.toLocaleDateString(locale, options);
    const endStr = endDate.toLocaleDateString(locale, options);

    return `${startStr} - ${endStr}`;
  };

  // Handler for week navigation
  const handlePreviousWeek = async () => {
    try {
      await navigateWeek('prev');
    } catch (err) {
      // Error is handled by store
    }
  };

  const handleNextWeek = async () => {
    try {
      await navigateWeek('next');
    } catch (err) {
      // Error is handled by store
    }
  };

  // Handler for meal slot clicks - opens RecipePicker modal
  const handleSlotClick = (dayIndex: DayOfWeek, mealType: MealType, slot: MealSlotType | null) => {
    setSelectedSlot({ dayIndex, mealType, slot });
    setIsModalOpen(true);
  };

  // Handler for closing RecipePicker modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  if (error) {
    return (
      <div className="meal-plan-container">
        <div className="error-message" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="meal-plan-container">
      <header className="meal-plan-header">
        <div className="meal-plan-header-content">
          <h1>{t('weeklyMealPlan')}</h1>
          <p className="meal-plan-subtitle">
            {t('planSubtitle')}
          </p>
        </div>
        {currentMealPlan && (
          <nav className="week-navigation" aria-label="Week navigation">
            <button
              className="week-nav-button"
              onClick={handlePreviousWeek}
              disabled={isLoading}
              aria-label="Go to previous week"
            >
              <span className="nav-arrow" aria-hidden="true">‹</span>
              <span className="nav-text">{t('previous')}</span>
            </button>
            <div className="date-range" aria-live="polite" aria-atomic="true">
              {formatDateRange()}
            </div>
            <button
              className="week-nav-button"
              onClick={handleNextWeek}
              disabled={isLoading}
              aria-label="Go to next week"
            >
              <span className="nav-text">{t('next')}</span>
              <span className="nav-arrow" aria-hidden="true">›</span>
            </button>
          </nav>
        )}
      </header>

      {isLoading ? (
        <div className="loading-state" role="status" aria-live="polite">
          <p>{t('loadingMealPlan')}</p>
        </div>
      ) : isTransposed ? (
        /* Transposed layout: rows = days, columns = meal types (mobile & portrait tablet) */
        <main
          className="calendar-grid calendar-grid--transposed"
          role="grid"
          aria-label="Weekly meal plan calendar"
        >
          {/* Header row: empty corner + meal type headers */}
          <div className="calendar-header-row calendar-header-row--transposed" role="row">
            <div className="empty-cell" role="columnheader" />
            {MEALS.map((meal) => (
              <div key={meal} className="meal-header meal-header--col" role="columnheader">
                {t(meal)}
              </div>
            ))}
          </div>

          {/* Day rows */}
          {DAYS_KEYS.map((dayKey, dayIndex) => (
            <div key={dayKey} className="day-row" role="row">
              <div className="day-header day-header--row" role="rowheader">
                {t(dayKey)}
              </div>

              {MEALS.map((meal) => {
                const slot = getMealSlot(dayIndex as DayOfWeek, meal);
                return (
                  <MealSlot
                    key={`${dayKey}-${meal}`}
                    slot={slot}
                    day={t(dayKey)}
                    dayIndex={dayIndex as DayOfWeek}
                    mealType={meal}
                    onSlotClick={handleSlotClick}
                  />
                );
              })}
            </div>
          ))}
        </main>
      ) : (
        /* Default layout: rows = meal types, columns = days (desktop & landscape tablet) */
        <main
          className="calendar-grid"
          role="grid"
          aria-label="Weekly meal plan calendar"
        >
          {/* Header row with empty cell and day headers */}
          <div className="calendar-header-row" role="row">
            <div className="empty-cell" role="columnheader" />
            {DAYS_KEYS.map((dayKey) => (
              <div key={dayKey} className="day-header" role="columnheader">
                {t(dayKey)}
              </div>
            ))}
          </div>

          {/* Meal rows */}
          {MEALS.map((meal) => (
            <div key={meal} className="meal-row" role="row">
              <div className="meal-header" role="rowheader">
                {t(meal)}
              </div>

              {/* Meal slots for each day */}
              {DAYS_KEYS.map((dayKey, dayIndex) => {
                const slot = getMealSlot(dayIndex as DayOfWeek, meal);

                return (
                  <MealSlot
                    key={`${dayKey}-${meal}`}
                    slot={slot}
                    day={t(dayKey)}
                    dayIndex={dayIndex as DayOfWeek}
                    mealType={meal}
                    onSlotClick={handleSlotClick}
                  />
                );
              })}
            </div>
          ))}
        </main>
      )}

      {/* RecipePicker Modal */}
      {selectedSlot && currentMealPlan && (
        <RecipePicker
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          dayIndex={selectedSlot.dayIndex}
          mealType={selectedSlot.mealType}
          currentSlot={selectedSlot.slot}
          mealPlanId={currentMealPlan.id}
        />
      )}
    </div>
  );
}

export default WeeklyCalendar;
